import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { outlineApi } from '../../api/outlines';
import SectionTree from './SectionTree';
import SectionDetail from './SectionDetail';
import TemplateSelector from './TemplateSelector';
import AddSectionModal from './AddSectionModal';
import WorkshopCreator from './WorkshopCreator';
import VersionHistory from './VersionHistory';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import ModelPicker from '../Common/ModelPicker';
import './Outline.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';
import { usePaidConfirm } from '../Common/usePaidConfirm';

/**
 * OutlinePanel - Displays and manages outlines for stories or projects.
 *
 * Props:
 * - storyId: UUID of story (for story-level outline)
 * - projectId: UUID of project (for project-level outline)
 * - medium: Medium type (novel) - required for project-level
 *
 * Exactly one of storyId or projectId should be provided.
 */
export function OutlinePanel({ storyId, projectId, medium }) {
  const [outline, setOutline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showWorkshopCreator, setShowWorkshopCreator] = useState(false);
  const [workshopSection, setWorkshopSection] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const { confirm, modalProps } = useConfirm();
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const navigate = useNavigate();

  // Determine if this is a project-level outline
  const isProjectLevel = !!projectId;

  const loadOutline = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = isProjectLevel
        ? await outlineApi.getProjectOutline(projectId)
        : await outlineApi.getOutline(storyId);
      // Backend returns { data: outline }, extract the data
      setOutline(response?.data || response);
    } catch (err) {
      if (err.status === 404) {
        setOutline(null);
      } else {
        setError('Failed to load outline');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [storyId, projectId, isProjectLevel]);

  useEffect(() => {
    loadOutline();
  }, [loadOutline]);

  const handleCreateOutline = async (templateKey, selectedMedium = null) => {
    try {
      setLoading(true);
      if (isProjectLevel) {
        // Project-level requires medium
        const mediumToUse = selectedMedium || medium;
        await outlineApi.createProjectOutline(projectId, mediumToUse, templateKey);
      } else {
        await outlineApi.createOutline(storyId, templateKey);
      }
      await loadOutline();
      setShowTemplateSelector(false);
    } catch (err) {
      setError('Failed to create outline');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReorderSections = async (sectionIds, parentSectionId) => {
    try {
      await outlineApi.reorderSections(outline.id, sectionIds, parentSectionId);
      await loadOutline();
    } catch (err) {
      console.error('Failed to reorder:', err);
    }
  };

  const handleSectionDeleted = (sectionId) => {
    if (selectedSection?.id === sectionId) {
      setSelectedSection(null);
    }
    loadOutline();
  };

  const handleCreateWorkshop = (section) => {
    setWorkshopSection(section);
    setShowWorkshopCreator(true);
  };

  const handleWorkshopCreated = (workshop) => {
    setShowWorkshopCreator(false);
    setWorkshopSection(null);
    // Navigate to the appropriate chat route
    navigate(`/chat/${workshop.id}`);
  };

  const handleGenerateAll = async () => {
    const paidOk = await askPaid('outline_all');
    if (!paidOk) return;
    const ok_gen = await confirm({ title: 'Generate content?', message: 'Generate content for all sections? This may take a while.', confirmLabel: 'Generate' });
    if (!ok_gen) return;
    try {
      setGenerating(true);
      await outlineApi.generateOutline(outline.id);
      await loadOutline();
    } catch (err) {
      setError('Failed to generate outline');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = (versionNumber, format) => {
    const url = outlineApi.getExportUrl(outline.id, format, versionNumber);
    window.open(url, '_blank');
  };

  // Version viewing is handled internally by VersionHistory component

  const handleDeleteOutline = async () => {
    const confirmMsg = isProjectLevel
      ? 'Delete this series outline? All sections will be permanently removed.'
      : 'Delete this outline? All sections will be permanently removed.';
    const ok_del = await confirm({ title: 'Delete outline?', message: confirmMsg, confirmLabel: 'Delete', destructive: true });
    if (!ok_del) return;

    try {
      setLoading(true);
      await outlineApi.deleteOutline(outline.id);
      setOutline(null);
      setSelectedSection(null);
    } catch (err) {
      setError('Failed to delete outline');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <div className="outline-panel__error">{error}</div>;

  // No outline exists - show template selector
  if (!outline) {
    if (showTemplateSelector) {
      return (
        <div className="outline-panel">
          <TemplateSelector
            medium={medium}
            isProjectLevel={isProjectLevel}
            onSelect={handleCreateOutline}
            onCancel={() => setShowTemplateSelector(false)}
          />
        </div>
      );
    }

    const emptyTitle = isProjectLevel ? 'No Series Outline Yet' : 'No Outline Yet';
    const emptyText = isProjectLevel
      ? 'Create a series outline to plan your multi-book arc.'
      : "Create an outline to organize your story's structure.";

    return (
      <div className="outline-panel outline-panel--empty">
        <div className="outline-panel__empty-state">
          <h2>{emptyTitle}</h2>
          <p>{emptyText}</p>
          <Button onClick={() => setShowTemplateSelector(true)} variant="primary">
            Create Outline
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isProjectLevel ? 'Series Outline' : 'Story Outline';

  return (
    <div className="outline-panel">
      <div className="outline-panel__header">
        <h2>{headerTitle}</h2>
        <div className="outline-panel__header-actions">
          <Button onClick={() => setShowAddSection(true)} variant="secondary">
            Add Section
          </Button>
          <ModelPicker mode="select" functionKey="story_board_generation" disabled={generating} />
          <Button
            onClick={handleGenerateAll}
            disabled={generating}
            variant="primary"
          >
            {generating ? 'Generating...' : 'Generate All'}
          </Button>
          <Button
            onClick={() => setShowVersions(!showVersions)}
            variant={showVersions ? 'primary' : 'secondary'}
          >
            Versions
          </Button>
          <Button onClick={handleDeleteOutline} variant="danger">
            Delete Outline
          </Button>
        </div>
      </div>

      <div className="outline-panel__content">
        <div className="outline-panel__tree">
          <SectionTree
            sections={outline.sections}
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
            onReorder={handleReorderSections}
            isProjectLevel={isProjectLevel}
          />
        </div>

        <div className="outline-panel__detail">
          {showVersions ? (
            <VersionHistory
              outlineId={outline.id}
              onExport={handleExport}
            />
          ) : (
            <SectionDetail
              section={selectedSection}
              onUpdate={loadOutline}
              onDelete={handleSectionDeleted}
              onCreateWorkshop={handleCreateWorkshop}
            />
          )}
        </div>
      </div>

      {showAddSection && (
        <AddSectionModal
          outlineId={outline.id}
          parentSection={isProjectLevel ? null : selectedSection}
          isProjectLevel={isProjectLevel}
          onClose={() => setShowAddSection(false)}
          onCreated={(section) => {
            setShowAddSection(false);
            loadOutline();
            setSelectedSection(section);
          }}
        />
      )}

      {showWorkshopCreator && workshopSection && (
        <WorkshopCreator
          section={workshopSection}
          onClose={() => {
            setShowWorkshopCreator(false);
            setWorkshopSection(null);
          }}
          onCreated={handleWorkshopCreated}
        />
      )}
      {paidDialog}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default OutlinePanel;
